import { Alignment, ArrayUtil, Block, DOMUtil, FieldUpdateEvent, getLogger, getThemeColor, getThemeForKey, HorizontalAlignment, Keys, ModelRow, ScrollOptions, VerticalAlignment } from "@mcleod/core";
import { Button, Cursor, DataSourceMode, deserializeComponents, HorizontalSpacer, Image, KeyEvent, KeyHandler, Label, Layout, Panel, ResizeListener, Textbox } from "../..";
import { CloneComponent } from "../../base/CloneComponent";
import { Component } from "../../base/Component";
import { ComponentTypes } from "../../base/ComponentTypes";
import { Container } from "../../base/Container";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { SelectionMode, SelectionType } from "../../base/SelectionMode";
import { ValidationResult } from "../../base/ValidationResult";
import { ClickEvent } from "../../events/ClickEvent";
import { DomMouseEvent } from "../../events/DomEvent";
import { Event } from "../../events/Event";
import { KeyHandlerGroup } from "../../events/KeyHandlerGroup";
import { MouseEvent } from "../../events/MouseEvent";
import { TableAction, TableContentsChangedEvent } from "../../events/TableContentsChangedEvent";
import { TableRowBeforeSaveEvent } from "../../events/TableRowBeforeSaveEvent";
import { TableRowCreationEvent } from "../../events/TableRowCreationEvent";
import { TableRowDisplayEvent } from "../../events/TableRowDisplayEvent";
import { EditRowDecorator } from "../../page/decorators/EditRowDecorator";
import { Overlay } from "../../page/Overlay";
import { ButtonVariant } from "../button/ButtonVariant";
import { AddType } from "./AddType";
import { EllipsisPopup } from "./EllipsisPopup";
import { RowModeControlType } from "./RowModeControlType";
import { Table } from "./Table";
import { TableCell } from "./TableCell";
import { TableColumn } from "./TableColumn";
import { TableRowPropDefinitions, TableRowProps } from "./TableRowProps";
import { TableRowStyles } from "./TableRowStyles";

const log = getLogger("components.table.TableRow");

export enum TableRowMode {
    NONE = "none",
    ADD = "add",
    UPDATE = "update",
    SEARCH = "search",
    QUICK_ADD = "quickAdd"
}

export class TableRow extends Component implements TableRowProps {
    public table: Table;
    public _populatedDOM: boolean;
    private _expanded: boolean;
    public cells: TableCell[];
    private _placeholderHeight: number;
    private expanderButton: Button;
    private _placeholder: HTMLTableCellElement;
    public expandable: boolean;
    public data: any;
    private _originalData: any;
    private _index: number;
    private _selected: boolean;
    private expanderAlignment: Alignment;
    private expanderPanel: Panel;
    private _mode: TableRowMode;
    private _canBeEdited: boolean;
    private _canBeDeleted: boolean;
    private _toolsAlignment: HorizontalAlignment;
    public _toolsPanel: Panel;
    private _expanderCellIndex: number = -1;
    private _toolsCellIndex: number = -1;
    private _ellipsisButton: Button;
    private _ellipsisPopup: EllipsisPopup;
    private _ellipsisPopupOverlay: Overlay;
    public virtualized: boolean;
    private _unexpandedBorderBottomWidth: string;
    private _dragHandlerImage: Image;
    private _dragSession: DragSession;
    private lastSelClick: number;
    private _keyHandlerGroup: KeyHandlerGroup;
    private expansionResizeListenerRef: ResizeListener = () => this.resizeExpansionComponent();

    constructor(table: Table, props: Partial<TableRowProps>) {
        super("div", props);
        this.table = table;
        this._populatedDOM = false;
        this._expanded = false;
        this.cells = [];
        this._element.classList.add(TableRowStyles.base);
        this.addClickListener(event => this.rowClicked(event), true);
        if (props.virtualized !== false)
            this.addVirtualizedPlaceholder();
        (this._element as any).__row = this; // give a reference to the row in its element so we can easily access it from the observer
        this.canBeEdited = this.table.allowEdit;
        this.canBeDeleted = this.table.allowEdit;
        if (this.table.selectionMode !== SelectionMode.NONE || this.table.addType === AddType.QUICK)
            this._element.classList.add(TableRowStyles.hoverShade);
        this._mode = TableRowMode.NONE;
        this.setProps(props);
    }

    override setProps(props: Partial<TableRowProps>) {
        super.setProps(props);
    }

    get toolsPanel(): Panel {
        return this._toolsPanel;
    }

    get placeholderHeight(): number {
        return this._placeholderHeight;
    }

    set placeholderHeight(value: number) {
        this._placeholderHeight = value;
        if (this._placeholder != null) {
            if (value == null)
                this._placeholder.style.height = "";
            else
                this._placeholder.style.height = value + "px";
        }
    }

    get expanded(): boolean {
        return this._expanded;
    }

    set expanded(value: boolean) {
        if (value == null)
            value = false;
        if (this._expanded === value)
            return;
        this._expanded = value;
        if (value)
            this.populateDOMIfNeeded();
        if (this.expanderButton != null) {
            if (this.expanderAlignment === Alignment.LEFT)
                this.expanderButton.caption = this.expanded ? "-" : "+";
            else
                this.expanderButton.imageRotation = this.expanded ? 0 : 90;
        }
        this.table.handleRowExpansion(this);
    }

    private resizeExpansionComponent() {
        const expansionRow = this.getExpansionElement();
        if (expansionRow != null)
            expansionRow.style.width = DOMUtil.getElementWidthString(this._element);
    }

    setExpanded(value) {
        this.expanded = value;
    }

    addVirtualizedPlaceholder() {
        this._placeholder = document.createElement("td");
        this._element.appendChild(this._placeholder);
    }

    populateDOM() {
        delete this._placeholder;
        this._element.innerHTML = "";
        const creationEvent = new TableRowCreationEvent(this, this.table);
        this.table.fireRowCreationListeners(creationEvent);
        if (this.table.expanderAlignment === Alignment.LEFT && this.expandable === true)
            this._addExpanderCell();
        if (this.table._designer == null && this.toolsAlignment === HorizontalAlignment.LEFT)
            this._addToolsCell();
        for (let i = 0; i < this.table._columns.length; i++)
            this.populateDOMCell(this.table._columns[i]);
        if (this.table.expanderAlignment !== Alignment.LEFT && this.expandable === true)
            this._addExpanderCell();
        if (this.table._designer == null && this.toolsAlignment !== HorizontalAlignment.LEFT)
            this._addToolsCell();
        this._setDefaultFirstLastCellPadding();
        if (this.table._designer != null && this.table._designer.allowsDrop) {
            const designerGutter = document.createElement("td");
            designerGutter.style.width = "24px";
            designerGutter.style.verticalAlign = "bottom";
            this._element.appendChild(designerGutter);
            const rowPropsLabel = new Label({ caption: "Row Props", fontBold: true, fontSize: "small", cursor: Cursor.POINTER, color: "primary", wrap: false, style: { transform: "rotate(-90deg)" }, verticalAlign: VerticalAlignment.CENTER, fillRow: false });
            designerGutter.appendChild(rowPropsLabel._element);
        }

        this._populatedDOM = true;
        this._updateCellsBasedOnSelection();
        const displayEvent = new TableRowDisplayEvent(this, this.table);
        this.table.fireRowDisplayListeners(displayEvent);
    }

    private _setDefaultFirstLastCellPadding() {
        if (this.cells == null || this.cells.length === 0)
            return;
        const firstCell = this.cells[0];
        if (firstCell.paddingLeft == null)
            firstCell.paddingLeft = 12;
        const lastCell = this.cells[this.cells.length - 1];
        if (lastCell != null && lastCell.paddingRight == null)
            lastCell.paddingRight = 12;
    }

    private _addToolsCell(): void {
        const toolsCell = this._getToolsCell();
        if (toolsCell != null) {
            this._element.appendChild(toolsCell._element);
        }
    }

    private _addExpanderCell(): void {
        const expanderCell = this._getExpanderCell();
        if (expanderCell != null) {
            this._element.appendChild(expanderCell._element);
        }
    }

    clear() {
        this.cells = [];
        this._element.innerHTML = "";
        this._populatedDOM = false;
    }

    columnsChanged() {
        if (this._populatedDOM) {
            this.cells = [];
            this._element.innerHTML = "";
            this.populateDOM();
        }
    }

    populateDOMIfNeeded() {
        if (this._populatedDOM === true)
            return;
        this.populateDOM();
    }

    createCell(col: TableColumn): TableCell {
        let cell: TableCell;
        if (col.cellDef != null) {
            const def = col.cellDef;
            const id = col.cellDef?.def?.id || this.table.id + "-" + this._index + "-" + col.index;
            const props = {
                id: id,
                ...def.cellProps,
                table: this.table,
                row: this,
                col: col,
                boundRow: this.data
            };
            cell = deserializeComponents(def.owner, def.def, this._designer, props, null, null)[0]; //do not pass dataSources to deserialization; we don't want components in the row to be bound directly to the DataSource
        } else {
            cell = new TableCell({ table: this.table, row: this, _designer: this._designer, col: col, boundRow: this.data });
            if (typeof col.cell === "function") {
                const props = col.cell(this);
                if (Array.isArray(props) || props._element != null) {
                    cell.add(props);
                    // establishOwnership(props, this);
                }
                else {
                    cell.setProps(props);
                    if (props.id != null)
                        this[props.id] = cell;
                }
            }
            else {
                if (col.cell instanceof Component) {
                    CloneComponent.clone({ component: col.cell, targetComponent: cell, appendComponentId: this._designer == null });
                    if (col.cell.id != null)
                        this[col.cell.id] = col.cell;
                }
                else {
                    cell.setProps(col.cell);
                    if (col.cell.id != null)
                        this[col.cell.id] = cell;
                }
            }
        }
        if (this.table._designer == null) {
            cell.displayData(this.data, this.table.data, this._index);
            if (this.table.id !== "tableProps") {
                cell.printable = this.rowIsPrintable();
            }
        }
        return cell;
    }

    populateDOMCell(col: TableColumn) {
        const cell = this.createCell(col);
        this.cells.push(cell);
        this._element.appendChild(cell._element);
    }

    removeColumn(index: number) {
        if (this._populatedDOM === true) {
            const cell = this.cells[index];
            this._element.removeChild(cell._element);
            this.cells.splice(index, 1);
        }
    }

    switchColumns(index1: number, index2: number) {
        if (this._populatedDOM === true) {
            ArrayUtil.switchArrayElements(this.cells, index1, index2);
            DOMUtil.switchDOMElements(this._element.childNodes[index1], this._element.childNodes[index2]);
        }
    }

    get mode(): TableRowMode {
        return this._mode;
    }

    set mode(value: TableRowMode) {
        if (value !== this._mode) {
            this._mode = value;

            if (this._mode !== TableRowMode.SEARCH) {
                if (this._toolsCellIndex >= 0) {
                    this._fillToolsPanel(this.cells[this._toolsCellIndex]);
                }

                for (const cell of this.cells) {
                    cell.printable = this.rowIsPrintable();
                }
            }
            else {
                for (const cell of this.cells) {
                    cell.printable = false;
                }
            }
        }
    }

    rowBeingEdited(): boolean {
        return this._mode === TableRowMode.ADD || this._mode === TableRowMode.UPDATE;
    }

    rowIsPrintable(): boolean {
        return this._mode === TableRowMode.NONE;
    }

    private _getToolsCell(): TableCell {
        if ((this.canBeEdited !== true && this.canBeDeleted !== true) || this.mode === TableRowMode.SEARCH) {
            return null;
        }
        const cell = new TableCell({ table: this.table, row: this, _designer: this._designer, id: "rowTools" + this._index });
        this.table._applyCellProps(cell);
        this._fillToolsPanel(cell);
        this.cells.push(cell);
        this._toolsCellIndex = this.cells.length - 1;
        this.table.toolsCellAdded(cell.width as number);
        return cell;
    }

    get toolsAlignment(): HorizontalAlignment {
        return this._toolsAlignment || HorizontalAlignment.RIGHT;
    }

    set toolsAlignment(value: HorizontalAlignment) {
        if (value === this._toolsAlignment) {
            return;
        }
        this.toolsAlignment = value;
    }

    private _fillToolsPanel(cell: TableCell) {
        let tableCellWidth = null;
        if (this._toolsPanel != null) {
            cell.remove(this._toolsPanel);
        }

        this._toolsPanel = new Panel({ padding: 0, width: 200, id: "tableRowTools" });

        if (this.mode === TableRowMode.QUICK_ADD) {
            if (this.table.sequenceField != null)
                this._toolsPanel.add(new HorizontalSpacer());
            if (this.table.editLayout != null)
                this._toolsPanel.add(this._getSlideoutButton());
            const quickAddButton = new Button({ ...this._getQuickAddButtonProps() });
            quickAddButton.addHandlerForKey(Keys.ENTER);
            quickAddButton.addHandlerForKey(Keys.ENTER, { ctrlKey: true });
            quickAddButton.addClickListener((event: Event) => {
                this.persistQuickAddRow();
            });
            this._toolsPanel.add(quickAddButton);
        }
        else if (this.table.rowModeControlType === RowModeControlType.AUTO ||
            this.table.rowModeControlType === RowModeControlType.ALWAYS_EDIT) {
            if (this.mode !== TableRowMode.SEARCH) {
                if (this.table.sequenceField != null) {
                    if (this.draggable === true)
                        this._toolsPanel.add(this._getDragHandlerImage());
                    else
                        this._toolsPanel.add(new HorizontalSpacer());
                }
                if (this._canBeEdited && this.table.editLayout != null)
                    this._toolsPanel.add(this._getSlideoutButton());
                if (this._canBeDeleted) {
                    const deleteButton = new Button({ themeKey: "tableRow.deleteButton", rowBreak: false, focusable: true });
                    deleteButton.addClickListener((event: Event) => {
                        this.deleteRow();
                    });
                    this._toolsPanel.add(deleteButton);
                }
            }
        }
        else if (this.table.rowModeControlType === RowModeControlType.MANUAL) {
            if (this.mode === TableRowMode.ADD || this.mode === TableRowMode.UPDATE) {
                tableCellWidth = 100;
                const saveButton = new Button({ ...this._getSaveButtonProps() });
                saveButton.addClickListener((event: Event) => {
                    this.saveChanges();
                });
                this._toolsPanel.add(saveButton);

                const cancelButton = new Button({ ...this._getCancelButtonProps() });
                cancelButton.addClickListener((event: Event) => {
                    this.cancelEdit();
                });
                this._toolsPanel.add(cancelButton);
            }
            else if (this.mode === TableRowMode.NONE) {
                this._ellipsisButton = new Button({ ...this._getEllipsisButtonProps() });
                this._ellipsisButton.addClickListener((event: Event) => {
                    this._toggleEllipsisPopup();
                });
                this._toolsPanel.add(this._ellipsisButton);
            }
        }
        cell.width = tableCellWidth ?? Math.max(this._toolsPanel.getComponentCount(), 1) * 38;
        cell.add(this._toolsPanel);
    }

    /**
     * Creates the drag handler image for the row.  This button, when present, is shown within the tools cell.
     */
    private _getDragHandlerImage(): Image {
        if (this._dragHandlerImage == null) {
            this._dragHandlerImage = new Image({
                themeKey: "tableRow.dragHandler",
                rowBreak: false
            });
            this._dragHandlerImage.addMouseDownListener(event => this._dragHandlerMouseDownListener(event));
        }
        return this._dragHandlerImage;
    }

    /**
     * Begins the drag-to-resequence action (when the drag handler image is clicked)
     *
     * @param event: the MouseEvent from the onmousedown action
     */
    private _dragHandlerMouseDownListener(event: MouseEvent) {
        log.debug("Start row drag, moving row at index %o", this._index);
        event.preventDefault();
        this._createDragSession(event);
        // log.debug("Current row top: %o, next move up at %o, next move down at %o", this._element.getBoundingClientRect().top, this._dragSession.nextMoveUpAt, this._dragSession.nextMoveDownAt);
        this._element.style.zIndex = (Number.parseInt(this._dragSession.previousZIndex) + 1) + "";
        this._element.draggable = true;
        this.table.setTableBodyOnMouseMove(event => this._dragHandlerMouseMoveListener(event));
        document.onmouseup = (event => this._dragHandlerMouseUpListener(event));
    }

    /**
     * Creates a DragSession object to hold various values that are useful during the drag-to-resequence action
     *
     * @param event: the MouseEvent from the onmousedown action
     */
    private _createDragSession(event: MouseEvent) {
        this._dragSession = new DragSession();
        this._dragSession.oldRowIndex = this.index;
        this._dragSession.newRowIndex = this.index;
        this._dragSession.rowHeight = DOMUtil.getElementHeight(this._element);
        this._dragSession.initialRowY = this._element.getBoundingClientRect().top;
        this._dragSession.nextMoveUpAt = this._element.getBoundingClientRect().top - (this._dragSession.rowHeight / 2);
        this._dragSession.nextMoveDownAt = this._element.getBoundingClientRect().bottom + (this._dragSession.rowHeight / 2);
        this._dragSession.previousZIndex = this._element.style.zIndex;
        this._dragSession.dragStartY = event.domEvent.clientY;
    }

    /**
     * During the drag-to-resequence action:
     *    - move the row to its new position (by setting its top property relative to its starting position)
     *    - if the row being dragged moves high/low enough to displace another row, set the top property on that other row
     *    - keep track of:
     *         - how high/low the row must go before it displaces another row
     *         - the new index of the row being dragged
     *
     * @param event: the MouseEvent from the onmousemove action
     */
    private _dragHandlerMouseMoveListener(event) {
        event.preventDefault();
        // log.debug("Drag mouse move dragStartY: %o, event.clientY: %o", this._dragStartY, event.clientY);
        const topValue = event.clientY - this._dragSession.dragStartY;
        // log.debug("New top value for row: %o, clientY: %o, dragStartY: %o", topValue, event.clientY, this._dragStartY);
        if ((topValue + this._dragSession.initialRowY) < this._dragSession.nextMoveUpAt) {
            this._dragSession.newRowIndex--;
            const otherRowToMove = this._getRowContainingY(this._dragSession.nextMoveUpAt);
            if (otherRowToMove != null) {
                const otherRowTop = DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("top", otherRowToMove));
                otherRowToMove.style.top = (otherRowTop + this._dragSession.rowHeight) + "px";
            }
            log.debug("Row moved up, new row index: %o", this._dragSession.newRowIndex);
            this._dragSession.nextMoveUpAt -= this._dragSession.rowHeight;
            this._dragSession.nextMoveDownAt -= this._dragSession.rowHeight
            // log.debug("Current row top: %o, next move up at %o, next move down at %o", this._element.getBoundingClientRect().top, this._nextMoveUpAt, this._nextMoveDownAt);
        }
        else if ((topValue + this._dragSession.rowHeight + this._dragSession.initialRowY) > this._dragSession.nextMoveDownAt) {
            this._dragSession.newRowIndex++
            const otherRowToMove = this._getRowContainingY(this._dragSession.nextMoveDownAt);
            if (otherRowToMove != null) {
                const otherRowTop = DOMUtil.getStyleAttrAsNumber(DOMUtil.getComputedStyle("top", otherRowToMove));
                otherRowToMove.style.top = (otherRowTop - this._dragSession.rowHeight) + "px";
            }
            log.debug("Row moved down, new row index: %o", this._dragSession.newRowIndex);
            this._dragSession.nextMoveUpAt += this._dragSession.rowHeight;
            this._dragSession.nextMoveDownAt += this._dragSession.rowHeight
            // log.debug("Current row top: %o, next move up at %o, next move down at %o", this._element.getBoundingClientRect().top, this._nextMoveUpAt, this._nextMoveDownAt);
        }
        this._element.style.top = topValue + "px";
        // log.debug("Drag mouse move new top value: %o", this._element.style.top);
    }

    /**
     * Find whatever row in the table contains the specified y position.  This helps to identify a row being displaced, so it can be moved.
     *
     * @param y: the y position value at which to look for a row
     */
    private _getRowContainingY(y: number): HTMLElement {
        for (const r of this.table.rows) {
            if (r._element === this._element)
                continue;
            const rect = r._element.getBoundingClientRect();
            if (rect.top < y && rect.bottom > y)
                return r._element;
        }
        return null;
    }

    /**
     * Ends the drag-to-resequence action (when the mouse button is released).  Removes temporary settings/values, including the DragSession object.
     *
     * @param event: the MouseEvent from the onmouseup action
     */
    private _dragHandlerMouseUpListener(event) {
        log.debug("End row drag")
        this._element.style.zIndex = this._dragSession.previousZIndex;
        this._element.draggable = false;
        this.table.moveRow(this, this._dragSession.newRowIndex, this._dragSession);
        this._dragSession = null;
        this.table.setTableBodyOnMouseMove(null);
        document.onmouseup = null;
    }

    private _getSlideoutButton(): Button {
        const slideoutButton = new Button({ themeKey: "tableRow.slideoutButton", rowBreak: false, focusable: true });
        slideoutButton.addKeyHandler(slideoutButton.createKeyHandler(Keys.ENTER, null, null, slideoutButton._element));
        slideoutButton.addClickListener((event: Event) => this._showEditLayout());
        return slideoutButton;
    }

    private _getSaveButtonProps() {
        return {
            margin: 0,
            width: 78,
            focusable: false,
            caption: "Save",
            marginBottom: 2,
            color: "primary"
        }
    }

    private _getCancelButtonProps() {
        return {
            margin: 0,
            width: 78,
            focusable: false,
            caption: "Cancel"
        }
    }

    private _getQuickAddButtonProps() {
        return {
            themeKey: "tableRow.quickAddButton",
            rowBreak: false,
            id: "quickAddButton",
            focusable: true
        };
    }

    private _getEllipsisButtonProps() {
        return {
            variant: ButtonVariant.round,
            focusable: false,
            margin: 2,
            rowBreak: false,
            color: "subtle.darker",
            imageName: "ellipsis"
        };
    }

    private _toggleEllipsisPopup() {
        if (this._ellipsisPopup == null) {
            this._ellipsisPopup = new EllipsisPopup(this, this.table);
            Overlay.alignToAnchor(this._ellipsisPopup, this._ellipsisButton, Alignment.BOTTOM);
            this._showEllipsisPopup(this._ellipsisPopup);
        }
        else {
            this._hideEllipsisPopup();
        }
    }

    private _showEllipsisPopup(ellipsisPopup: EllipsisPopup) {
        this._ellipsisPopupOverlay = Overlay.showInOverlay(ellipsisPopup, { onClose: () => this._hideEllipsisPopup() });
    }

    private _hideEllipsisPopup() {
        if (this._ellipsisPopupOverlay != null) {
            Overlay.hideOverlay(this._ellipsisPopupOverlay);
        }
        this._ellipsisPopup = null;
    }

    get canBeEdited(): boolean {
        return this._canBeEdited;
    }

    set canBeEdited(value: boolean) {
        this._canBeEdited = value;
    }

    get canBeDeleted(): boolean {
        return this._canBeDeleted
    }

    set canBeDeleted(value: boolean) {
        if (value !== this._canBeDeleted) {
            this._canBeDeleted = value;
            if (this._toolsCellIndex >= 0)
                this._fillToolsPanel(this.cells[this._toolsCellIndex]);
        }
    }

    editRow() {
        if (this.table.addType === AddType.QUICK)
            this.selected = true;
        if (this.mode === TableRowMode.QUICK_ADD || this.mode === TableRowMode.SEARCH) {
            return;
        }
        this.mode = TableRowMode.UPDATE;
        if (this.data instanceof ModelRow) {
            this._originalData = this.data.createBasicCopy();
        } else {
            this._originalData = { ...this.data };
        }

        this._hideEllipsisPopup();
    }

    cancelEdit() {
        if (this.mode === TableRowMode.UPDATE) {
            this.data = this._originalData.createBasicCopy();
            this._originalData = null;
            this.displayComponentValues();
            this.mode = TableRowMode.NONE;
        }
        else if (this.mode === TableRowMode.ADD) {
            if (this.table.data?.[this.index] === this.data)
                this.deleteRow();
            else
                this.table.removeRow(this.index);
        }
    }

    _showEditLayout(addingRowInSlideout: boolean = false) {
        if (this.mode === TableRowMode.ADD || this.mode === TableRowMode.UPDATE) {
            const changesValid = this.saveChanges();
            if (changesValid !== true)
                return;
        }

        const erd = new EditRowDecorator({
            layout: Layout.getLayout(this.table.editLayout),
            layoutLoadListeners: this.table.onEditLayoutLoaded != null ? (event: Event) => this.table.onEditLayoutLoaded(erd, this) : null,
            width: this.table.editLayoutWidth,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            data: this._copyDataForSlideout(),
            onDelete: this.mode !== TableRowMode.QUICK_ADD ? () => {
                this.deleteRow(() => erd.slideOut());
            } : null,
            onSave: (updatedData: ModelRow | any) => this.saveChangesFromEdit(updatedData),
            onAddAnother: () => this.editClearNewRow(erd),
            onClose: (cancelled: boolean) => this.onSlideoutClose(erd, cancelled, addingRowInSlideout)
        });
    }

    private _copyDataForSlideout(): ModelRow | any {
        if (this.data instanceof ModelRow)
            return this.data.createBasicCopy();
        return this.data;
    }

    public persistQuickAddRow() {
        if (this.mode !== TableRowMode.QUICK_ADD)
            return;
        this.saveChanges();
        this.updateDataFromComponents();
        this.table.saveQuickAddData();
    }

    public displayComponentValues(): void {
        for (const cell of this.cells)
            if (cell.components != null)
                for (const comp of cell.components)
                    comp.displayData(this.data, this.table.data, this.index);
    }

    public hasChanges(): boolean {
        return this._originalData != null;
    }

    saveChanges(afterSave?: () => any): boolean {
        if (this.table.addType === AddType.QUICK)
            this.selected = false;
        this.updateDataFromComponents();
        const event = new TableRowBeforeSaveEvent(this, this.table, this.table.getParentDataSource());
        this.table.fireRowBeforeSaveListeners(event);
        if (this.validateSimple(true) !== true) {
            return false;
        }
        if (this.data instanceof ModelRow && this.table.persistChangesImmediately) {
            const rowToSave = this.data as ModelRow;
            rowToSave.post().then(() => {
                if (afterSave) afterSave();
            });
        }
        this._originalData = null;

        if (this.mode !== TableRowMode.QUICK_ADD)
            this.table.fireContentsChangedListener(new TableContentsChangedEvent(this.table, TableAction.UPDATE));

        if (this.table.rowModeControlType !== RowModeControlType.ALWAYS_EDIT)
            this.mode = TableRowMode.NONE;
        return true;
    }

    editClearNewRow(erd: EditRowDecorator) {
        this.table._createNewRowData().then(newRowData => {
            const addRowResult = this.table.addRow(newRowData, { mode: TableRowMode.ADD }, { display: false, addToData: true });
            addRowResult.row._showEditLayout(true);
            this.onSlideoutClose(erd, false, true);
        });
    }

    saveChangesFromEdit(updatedData: ModelRow | any) {
        if (this.mode === TableRowMode.QUICK_ADD) {
            this.data.set(updatedData.data);
            const addRowResult = this.table.addRow(this.data, { mode: TableRowMode.ADD }, { display: true, addToData: true, save: false }); //save will happen afterward
            if (addRowResult.row.saveChanges()) {
                this.table._removeQuickAddRow();
                this.table._addQuickAddRow();
            }
        } else {
            this.data.set(updatedData.data);
            this.saveChanges()
            if (this.mode === TableRowMode.ADD)
                this.table.displayRow(this);
        }
        this.table.resetFilter();
    }

    onSlideoutClose(erd: EditRowDecorator, cancelled: boolean, addingRowInSlideout: boolean) {
        const slideout = () => erd.slideOut();
        if (cancelled === true && addingRowInSlideout === true)
            this.deleteRow(slideout);
        else
            slideout();
    }

    public updateDataFromComponents(): void {
        for (const cell of this.cells)
            if (cell.components != null)
                for (const comp of cell.components)
                    comp.updateBoundData(this.data, DataSourceMode.UPDATE);
    }

    private updateDataToComponents(addedRow: TableRow): void {
        let cells: TableCell[];
        let data: any;
        if (addedRow != null) {
            cells = addedRow.cells;
            data = addedRow.data;
        }
        else {
            cells = this.cells;
            data = this.data;
        }

        for (const cell of cells)
            if (cell.components != null)
                for (const comp of cell.components)
                    comp.displayData(data, data, 0);
    }

    deleteRow(afterDelete?: () => {}) {
        const event = new TableContentsChangedEvent(this.table, TableAction.DELETE);
        if (this.table.dataSource?.parentDataSource != null && !this.table.persistChangesImmediately) {
            this._hideEllipsisPopup();
            this.table.deleteRow(this.index);
            this.table.fireContentsChangedListener(event);
            if (afterDelete) afterDelete();
        }
        else {
            ReflectiveDialogs.showYesNo("Are you sure you want to delete this record?", "Confirm Deletion").then(clickedYes => {
                if (clickedYes) {
                    this._hideEllipsisPopup();
                    const modelRow: ModelRow = this.data;
                    this.table.deleteRow(this.index);
                    if (this.table.persistChangesImmediately)
                        modelRow.delete().then(() => this.table.fireContentsChangedListener(event));
                    if (afterDelete)
                        afterDelete();
                }
            });
        }
    }

    private _getExpanderCell(): TableCell {
        const cell = new TableCell({ table: this.table, row: this, _designer: this._designer, id: "expander" + this._index });
        this.table._applyCellProps(cell);
        cell.width = "40px";
        cell.verticalAlign = VerticalAlignment.CENTER;
        this.cells.push(cell);
        this._expanderCellIndex = this.cells.length - 1;
        this.expanderPanel = new Panel({ padding: 0 });
        this.expanderButton = new Button({
            variant: ButtonVariant.round,
            margin: 0,
            focusable: false,
            themeKey: this.expanderAlignment === Alignment.LEFT ? "tableRow.expanderButtonLeft" : "tableRow.expanderButtonRight"
        });
        this.expanderButton.addClickListener(event => this.expanded = !this.expanded);
        this.expanderPanel.add(this.expanderButton);
        cell.add(this.expanderPanel);
        //will probably need a method equivalent to table.toolsCellAdded(number) if we ever allow the expanderAlignment to be set to 'LEFT'
        cell._element.style.position = "sticky";
        cell._element.style.right = "0px";
        cell._element.style.backgroundColor = "inherit";
        return cell;
    }

    rowClicked(event: DomMouseEvent) {
        if (this.table != null) {
            if (this.table.allowEdit && this.table.rowModeControlType === RowModeControlType.AUTO) {
                if (this.mode !== TableRowMode.UPDATE && this.mode !== TableRowMode.ADD) {
                    if (this.table.completeEditedRows() === true)
                        this.editRow();
                }
                this.table.activateValidateOnOutsideClick(event as PointerEvent);
            }
            else if (this.table.rowModeControlType !== RowModeControlType.ALWAYS_EDIT) {
                const now = new Date().getTime(); // don't toggle the selection when the user is trying to Ctrl-dbl-click
                if (now - this.lastSelClick < 500)
                    return;
                this.lastSelClick = now;
                const clickEvent = new ClickEvent(this, event);
                const isCtrl = clickEvent.hasModifiers({ ctrlKey: true });
                const isShift = clickEvent.hasModifiers({ shiftKey: true });
                let selType = SelectionType.SINGLE;
                if (isShift)
                    selType = SelectionType.RANGE;
                else if (isCtrl)
                    selType = SelectionType.DISTINCT;
                this.table.selectRow(this, selType, event);
            }
        }
    }

    validateSimple(checkRequired: boolean = true, showErrors: boolean = true): boolean {
        const validationResults: ValidationResult[] = this.validate(checkRequired, showErrors);
        if (validationResults == null) {
            return true;
        }
        for (const validationResult of validationResults) {
            if (validationResult.isValid !== true) {
                return false;
            }
        }
        return true;
    }

    validate(checkRequired: boolean, showErrors: boolean = true): ValidationResult[] {
        let result: ValidationResult[] = null;
        for (let i = 0; i < this.cells.length; i++) {
            if (i === this._toolsCellIndex || i === this._expanderCellIndex) {
                continue;
            }
            const cell = this.cells[i];
            const thisResult = cell.validate(checkRequired, showErrors);
            if (thisResult !== null) {
                if (result == null)
                    result = thisResult;
                else
                    result = result.concat(thisResult);
            }
        }
        return result;
    }

    get index(): number {
        return this._index;
    }

    set index(value: number) {
        this._index = value;
    }

    get selected(): boolean {
        return this._selected;
    }

    set selected(value: boolean) {
        this._selected = value;
        this.backgroundColor = value ? getThemeForKey("table.selectionBackground") : "";
        if (this._populatedDOM === true)
            this._updateCellsBasedOnSelection();
    }

    private _updateCellsBasedOnSelection() {
        if (this.selected == null)
            return;
        for (const cell of this.cells)
            this._updateCellSelection(cell);
    }

    private _updateCellSelection(cell: TableCell) { // Move along.  Nothing to see here.
        if (!(cell instanceof Container))
            return;
        const comps: Component[] = cell.getRecursiveChildren();
        for (const comp of comps) {
            if (!(comp instanceof Button)) {
                if (this.selected) {
                    if (comp.color === "primary")
                        comp.color = "default.lighter";
                    if (this.table.addType === AddType.QUICK && comp instanceof Textbox && comp._inputDiv != null) {
                        let color = getThemeForKey("component.palette.table.editRow.textbox.backgroundColor");
                        color = getThemeColor(color) || color;
                        comp._inputDiv.style.backgroundColor = color;
                    }
                } else if (!this.selected && comp.color === "default.lighter") {
                    comp.color = "primary";
                }
            }
        }
    }

    override getPropertyDefinitions() {
        return TableRowPropDefinitions.getDefinitions();
    }

    public get populatedDOM() {
        return this._populatedDOM;
    }

    override getSearchValues(): string[] {
        const result = [];
        for (const cell of this.cells) {
            result.push(...cell.getSearchValues());
        }
        return result;
    }

    setPropOnChildren(propName: string, value: any) {
        for (const cell of this.cells)
            if (cell instanceof TableCell)
                cell.setPropOnChildren(propName, value);
    }

    setBorderBottom(isExpanding: boolean) {
        if (isExpanding === true) {
            this._unexpandedBorderBottomWidth = DOMUtil.getComputedStyle("border-bottom-width", this._element);
            this._element.style.borderBottomWidth = "0px";
        }
        else
            this._element.style.borderBottomWidth = this._unexpandedBorderBottomWidth;
    }

    override get serializationName() {
        return "tablerow";
    }

    override get draggable(): boolean {
        return this.table.rowsAreDraggable();
    }

    override set draggable(value: boolean) {
        if (this.cells != null && this.cells[this._toolsCellIndex] != null)
            this._fillToolsPanel(this.cells[this._toolsCellIndex]);
    }

    public static getContainingTableRow(component: Component): TableRow {
        if (component == null)
            return null;
        if (component instanceof TableCell)
            return component.row;
        return this.getContainingTableRow(component.parent);
    }

    public findComponentById(id: String | Function): Component {
        let result: Component;
        for (const cell of this.cells) {
            result = cell.findComponentById(id);
            if (result != null)
                break;
        }
        return result;
    }

    public findComponentsByField(field: string): Component[] {
        const components: Component[] = [];
        for (const cell of this.cells) {
            const cellComponents = cell.findComponentByField(field);
            for (const cellComponent of cellComponents) {
                components.push(cellComponent);
            }
        }
        return components;
    }

    public forEveryChildComponent(callback: (component: Component) => void) {
        for (const cell of this.cells)
            cell.forEveryChildComponent(callback);
    }

    displayDataForField(event: FieldUpdateEvent, data: ModelRow, allData: ModelRow[], rowIndex: number) {
        const components = this.findComponentsByField(event.fieldName);
        for (const component of components) {
            if (component !== event.originator)
                component.displayData(data, allData, rowIndex);
        }
    }

    getEffectiveDataSourceMode(): DataSourceMode {
        if (this.mode === TableRowMode.ADD || this.mode === TableRowMode.QUICK_ADD)
            return DataSourceMode.ADD;
        if (this.mode === TableRowMode.UPDATE)
            return DataSourceMode.UPDATE;
        if (this.mode === TableRowMode.SEARCH)
            return DataSourceMode.SEARCH;
        return DataSourceMode.NONE;
    }

    isFirstCellInRow(cell: TableCell): boolean {
        if (cell == null || this.cells == null)
            return false;
        const cellIndex = this.getIndexOfCell(cell);
        return cellIndex === 0;
    }

    isLastCellInRow(cell: TableCell): boolean {
        if (cell == null || this.cells == null)
            return false;
        const cellIndex = this.getIndexOfCell(cell);
        return cellIndex === this.cells.length - 1;
    }

    getIndexOfCell(cell: TableCell): number {
        if (cell == null || this.cells == null)
            return -1;
        for (let index = 0; index < this.cells.length; index++) {
            if (cell === this.cells[index])
                return index;
        }
        return -1;
    }

    public override scrollIntoView(options?: Partial<ScrollOptions>, startingElement?: HTMLElement) {
        if (this.expanded !== true)
            DOMUtil.scrollElementIntoView(this._element, options);
        else {
            if (options == null)
                options = {};
            if (options.block == null) {
                if (this.table.getBodyHeight() < this.getExpansionElementHeight())
                    options.block = Block.START;
                else
                    options.block = Block.END;
            }
            DOMUtil.scrollElementIntoView(this.getExpansionElement(), options);
        }
    }

    public getRowElementHeight(): number {
        return DOMUtil.getElementHeight(this._element);
    }

    public getExpansionComponent(): Component {
        return this["expansionComponent"] as Component;
    }

    _setExpansionComponent(value: Component) {
        this["expansionComponent"] = value;
        this.addResizeListener(this.expansionResizeListenerRef);
    }

    public getExpansionElement(): HTMLTableRowElement {
        return this["expansionRow"] as HTMLTableRowElement;
    }

    _setExpansionElement(value: HTMLTableRowElement) {
        this["expansionRow"] = value;
        if (value != null)
            value.style.width = DOMUtil.getElementWidthString(this._element);
    }

    public getExpansionElementHeight(): number {
        const expansionRow = this.getExpansionElement();
        if (expansionRow == null)
            return 0;
        return DOMUtil.getElementHeight(expansionRow);
    }

    public hideDragHandle() {
        if (this._dragHandlerImage != null)
            this._dragHandlerImage.visible = false;
    }

    public showDragHandle() {
        if (this._dragHandlerImage != null)
            this._dragHandlerImage.visible = true;
    }

    /**
     * This method will collect key handlers from this row and its child components.
     *
     * @returns void
     */
    fillKeyHandlerGroup(recreate: boolean = false) {
        if (this._keyHandlerGroup != null && recreate !== true)
            return;
        this._initKeyHandlerGroup(recreate);
        this._addKeyHandlersToGroup(this.getKeyHandlers());
        for (const cell of this.cells) {
            for (const component of cell.getRecursiveChildren()) {
                this._addKeyHandlersToGroup(component.getKeyHandlers());
            }
        }
        this._keyHandlerGroup.sort();
    }

    private _addKeyHandlersToGroup(compKeyHandlers: KeyHandler[]) {
        if (ArrayUtil.isEmptyArray(compKeyHandlers) === true)
            return;
        for (const compKeyHandler of compKeyHandlers) {
            this._keyHandlerGroup.addKeyHandler(compKeyHandler);
        }
    }

    private _initKeyHandlerGroup(recreate: boolean = false) {
        if (this._keyHandlerGroup == null || recreate === true)
            this._keyHandlerGroup = new KeyHandlerGroup();
    }

    handleKey(event: KeyEvent): boolean {
        this.fillKeyHandlerGroup();
        return this._keyHandlerGroup.handleKey(event);
    }

    public forEveryTableCell(callback: (cell: TableCell) => void) {
        for (const cell of this.cells) {
            callback(cell);
        }
    }

    public override doBeforeComponentEnlarge(cellsToIgnore: TableCell[]) {
        this["_tempHiddenCells"] = [];
        this.forEveryTableCell((cell: TableCell) => {
            if (cell.visible === true && cellsToIgnore.includes(cell) !== true) {
                this["_tempHiddenCells"].push(cell);
                cell.visible = false;
            }
        });
        this.enlarged = true;
    }

    public override doAfterComponentsShrink() {
        this.enlarged = false;
        for (const hiddenCell of this["_tempHiddenCells"]) {
            hiddenCell.visible = true;
        }
        delete this["_tempHiddenCells"];
    }

    public shrinkAllComponents() {
        for (const cell of this.cells) {
            cell.enlarged = false;
        }
    }

    override handleEnlargeOrShrink() {
        if (this.enlarged === true) {
            //allow the one row to fill the table body
            this["_tempMinHeight"] = this.minHeight;
            this.minHeight = this.table.getBodyHeight();
        }
        else {
            //shrink the row
            this.minHeight = this["_tempMinHeight"];
            delete this["_tempMinHeight"];
        }
    }

    override get enlargeScope(): Component {
        if (super.enlargeScope != null)
            return super.enlargeScope;
        super.enlargeScope = this.table; //this.table.getEnlargeScope();
        return super.enlargeScope;
    }

    public set enlargeScope(value: Component) {
        super.enlargeScope = value;
    }
}

export class DragSession {
    private _dragStartY: number;
    private _rowHeight: number;
    private _nextMoveUpAt: number;
    private _nextMoveDownAt: number;
    private _initialRowY: number;
    private _newRowIndex: number;
    private _oldRowIndex: number;
    private _previousZIndex: string;

    get dragStartY(): number {
        return this._dragStartY;
    }

    set dragStartY(value: number) {
        this._dragStartY = value;
    }

    get rowHeight(): number {
        return this._rowHeight;
    }

    set rowHeight(value: number) {
        this._rowHeight = value;
    }

    get nextMoveUpAt(): number {
        return this._nextMoveUpAt;
    }

    set nextMoveUpAt(value: number) {
        this._nextMoveUpAt = value;
    }

    get nextMoveDownAt(): number {
        return this._nextMoveDownAt;
    }

    set nextMoveDownAt(value: number) {
        this._nextMoveDownAt = value;
    }

    get initialRowY(): number {
        return this._initialRowY;
    }

    set initialRowY(value: number) {
        this._initialRowY = value;
    }

    get newRowIndex(): number {
        return this._newRowIndex;
    }

    set newRowIndex(value: number) {
        this._newRowIndex = value;
    }

    get oldRowIndex(): number {
        return this._oldRowIndex;
    }

    set oldRowIndex(value: number) {
        this._oldRowIndex = value;
    }

    get previousZIndex(): string {
        return this._previousZIndex;
    }

    set previousZIndex(value: string) {
        this._previousZIndex = value;
    }
}

ComponentTypes.registerComponentType("tablerow", TableRow.prototype.constructor);
